import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSessionContext } from '../context/SessionContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Button from '../components/ui/Button';
import { 
  FiArrowLeft, FiCalendar, FiClock, FiTarget, 
  FiTrendingUp, FiActivity, FiAward, FiChevronLeft, 
  FiChevronRight, FiCheckCircle, FiXCircle, FiAlertCircle 
} from 'react-icons/fi';
import { formatDuration, formatDate } from '../lib/utils';

const WeeklySummary = () => {
  const { sessions } = useSessionContext();
  const [selectedWeek, setSelectedWeek] = useState(0); // 0 = current week, -1 = last week, etc.

  // Calculate week data from actual sessions
  const weeklyData = useMemo(() => {
    const today = new Date();
    // Normalize today to avoid time-of-day issues
    today.setHours(12, 0, 0, 0);
    
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - today.getDay() + (selectedWeek * 7));
    weekStart.setHours(0, 0, 0, 0); // Start of Sunday

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999); // End of Saturday

    const weekRange = `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

    // Filter sessions for this week
    const weekSessions = sessions.filter((session) => {
      // Parse YYYY-MM-DD to a local date at noon for comparison
      const [year, month, day] = session.date.split('-').map(Number);
      const sessionDate = new Date(year, month - 1, day, 12, 0, 0);
      return sessionDate >= weekStart && sessionDate <= weekEnd;
    });

    if (weekSessions.length === 0) {
      return {
        weekRange,
        sessions: [],
        stats: {
          totalSessions: 0,
          totalMatTime: 0,
          totalWins: 0,
          totalLosses: 0,
          winRate: 0,
          averageSessionLength: 0,
          mostUsedTechniques: [],
          sessionsByType: []
        }
      };
    }

    // Calculate stats
    const totalSessions = weekSessions.length;
    const totalMatTime = weekSessions.reduce((sum, s) => sum + (s.duration || 0), 0);
    const totalWins = weekSessions.reduce((sum, s) => sum + (s.wins || 0), 0);
    const totalLosses = weekSessions.reduce((sum, s) => sum + (s.losses || 0), 0);
    const winRate = totalWins + totalLosses > 0 
      ? Math.round((totalWins / (totalWins + totalLosses)) * 100) 
      : 0;
    const averageSessionLength = Math.round(totalMatTime / totalSessions);

    // Calculate most used techniques
    const techniqueMap = {};
    weekSessions.forEach((session) => {
      session.techniques?.forEach((tech) => {
        if (!techniqueMap[tech.name]) {
          techniqueMap[tech.name] = { name: tech.name, count: 0, successes: 0 };
        }
        techniqueMap[tech.name].count++;
        if (tech.outcome === 'Succeeded') {
          techniqueMap[tech.name].successes++;
        }
      });
    });

    const mostUsedTechniques = Object.values(techniqueMap)
      .map((tech) => ({
        name: tech.name,
        count: tech.count,
        successRate: tech.count > 0 ? Math.round((tech.successes / tech.count) * 100) : 0
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);

    // Calculate sessions by type
    const typeMap = {};
    weekSessions.forEach((session) => {
      typeMap[session.sessionType] = (typeMap[session.sessionType] || 0) + 1;
    });
    const sessionsByType = Object.entries(typeMap).map(([type, count]) => ({ type, count }));

    return {
      weekRange,
      sessions: weekSessions,
      stats: {
        totalSessions,
        totalMatTime,
        totalWins,
        totalLosses,
        winRate,
        averageSessionLength,
        mostUsedTechniques,
        sessionsByType
      }
    };
  }, [sessions, selectedWeek]);

  const navigateWeek = (direction) => {
    setSelectedWeek(selectedWeek + direction);
  };

  const getOutcomeBadge = (outcome) => {
    const colors = {
      Succeeded: 'bg-green-100 text-green-700',
      Failed: 'bg-red-100 text-red-700',
      Attempted: 'bg-yellow-100 text-yellow-700',
      Taught: 'bg-blue-100 text-blue-700'
    };
    return colors[outcome] || 'bg-gray-100 text-gray-700';
  };

  const getOutcomeIcon = (outcome) => {
    switch (outcome) {
      case 'Succeeded': return <FiCheckCircle className="w-3 h-3" />;
      case 'Failed': return <FiXCircle className="w-3 h-3" />;
      case 'Attempted': return <FiAlertCircle className="w-3 h-3" />;
      default: return <FiTarget className="w-3 h-3" />;
    }
  };

  if (sessions.length === 0) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
        <div className="mb-6 sm:mb-8">
          <Link to="/">
            <Button variant="ghost" size="sm" className="mb-4">
              <FiArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
            </Button>
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Weekly Progress Summary</h1>
          <p className="text-sm sm:text-base text-slate-600">Analyze your training week and track improvements</p>
        </div>
        <div className="text-center py-12">
          <FiCalendar className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No Weekly Data Yet</h3>
          <p className="text-sm text-slate-600 mb-4">Start logging training sessions to see your weekly progress summaries.</p>
        </div>
      </div>
    );
  }

  if (weeklyData.sessions.length === 0) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
        <div className="mb-6 sm:mb-8">
          <Link to="/">
            <Button variant="ghost" size="sm" className="mb-4">
              <FiArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
            </Button>
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Weekly Progress Summary</h1>
              <p className="text-sm sm:text-base text-slate-600">Analyze your training week and track improvements</p>
            </div>
            <div className="flex items-center space-x-3">
              <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)}>
                <FiChevronLeft className="w-4 h-4" />
              </Button>
              <div className="text-center min-w-[180px]">
                <p className="text-sm font-medium text-slate-900">{weeklyData.weekRange}</p>
                <p className="text-xs text-slate-600">
                  {selectedWeek === 0 ? 'Current Week' : `${Math.abs(selectedWeek)} week${Math.abs(selectedWeek) > 1 ? 's' : ''} ago`}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigateWeek(1)} disabled={selectedWeek >= 0}>
                <FiChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
        <div className="text-center py-12">
          <FiActivity className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No Sessions This Week</h3>
          <p className="text-sm text-slate-600 mb-4">No training sessions logged for {weeklyData.weekRange}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="mb-6 sm:mb-8">
        <Link to="/">
          <Button variant="ghost" size="sm" className="mb-4">
            <FiArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Weekly Progress Summary</h1>
            <p className="text-sm sm:text-base text-slate-600">Analyze your training week and track improvements</p>
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)}>
              <FiChevronLeft className="w-4 h-4" />
            </Button>
            <div className="text-center min-w-[180px]">
              <p className="text-sm font-medium text-slate-900">{weeklyData.weekRange}</p>
              <p className="text-xs text-slate-600">
                {selectedWeek === 0 ? 'Current Week' : `${Math.abs(selectedWeek)} week${Math.abs(selectedWeek) > 1 ? 's' : ''} ago`}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigateWeek(1)} disabled={selectedWeek >= 0}>
              <FiChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-slate-600">Sessions</p>
              <FiActivity className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-slate-900">{weeklyData.stats.totalSessions}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-slate-600">Mat Time</p>
              <FiClock className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-slate-900">{formatDuration(weeklyData.stats.totalMatTime)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-slate-600">Win Rate</p>
              <FiAward className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-slate-900">{weeklyData.stats.winRate}%</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-slate-600">Record</p>
              <FiTrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-slate-900">{weeklyData.stats.totalWins}-{weeklyData.stats.totalLosses}</p>
            <p className="text-xs text-slate-500 mt-1">Avg {weeklyData.stats.averageSessionLength}m/session</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
        {weeklyData.stats.mostUsedTechniques.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base sm:text-lg flex items-center">
                <FiTarget className="w-5 h-5 mr-2 text-blue-600" />
                Most Used Techniques
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {weeklyData.stats.mostUsedTechniques.map((technique, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-slate-900 text-sm sm:text-base">{technique.name}</p>
                      <p className="text-xs sm:text-sm text-slate-600">Used {technique.count} times</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-green-600">{technique.successRate}%</p>
                      <p className="text-xs text-slate-500">success rate</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {weeklyData.stats.sessionsByType.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base sm:text-lg flex items-center">
                <FiActivity className="w-5 h-5 mr-2 text-purple-600" />
                Session Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {weeklyData.stats.sessionsByType.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-lg font-bold text-blue-600">{item.count}</span>
                      </div>
                      <span className="font-medium text-slate-900 text-sm sm:text-base">{item.type}</span>
                    </div>
                    <span className="text-xs sm:text-sm text-slate-500">
                      {Math.round((item.count / weeklyData.stats.totalSessions) * 100)}%
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base sm:text-lg flex items-center">
            <FiCalendar className="w-5 h-5 mr-2 text-blue-600" />
            This Week's Sessions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {weeklyData.sessions.map((session) => (
              <div key={session.id} className="p-4 border border-slate-200 rounded-lg hover:shadow-sm transition-shadow">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-3 sm:space-y-0 mb-3">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h4 className="font-semibold text-slate-900 text-sm sm:text-base">{session.sessionType}</h4>
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">{session.sessionType}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm text-slate-600">
                      <span className="flex items-center">
                        <FiCalendar className="w-3 h-3 mr-1" />
                        {formatDate(session.date)}
                      </span>
                      <span className="flex items-center">
                        <FiClock className="w-3 h-3 mr-1" />
                        {formatDuration(session.duration)}
                      </span>
                      {(session.wins > 0 || session.losses > 0) && (
                        <span className="flex items-center">
                          <FiTrendingUp className="w-3 h-3 mr-1 text-green-600" />
                          {session.wins}W - {session.losses}L
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {session.techniques && session.techniques.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {session.techniques.map((technique, idx) => (
                      <span key={idx} className={`px-2 py-1 text-xs rounded-full flex items-center space-x-1 ${getOutcomeBadge(technique.outcome)}`}>
                        {getOutcomeIcon(technique.outcome)}
                        <span>{technique.name}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WeeklySummary;